import { ArrowLeft, ArrowRight, ArrowUpRight, Clock3, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { ResponsiveImage, type ImageAssetName } from "./ResponsiveImage";
import { businessQuestions, servicePages, type ServiceContent } from "./service-content";
import "./service-pages.css";

const phone = "614-332-0622";
const phoneLink = "+16143320622";
const email = "support@housespace.us";

export default function ServicePage({ service }: { service: ServiceContent }) {
  const index = servicePages.findIndex((page) => page.slug === service.slug);
  const number = String(index + 1).padStart(2, "0");
  const questions = [...service.questions, ...businessQuestions];

  return (
    <>
      <a className="skip-link" href="#service-content">Skip to content</a>
      <div className="announcement"><span className="announcement-dot" /> Central & Southwest Ohio</div>
      <header className="site-header sp-header">
        <a className="wordmark" href="/" aria-label="HouseSpace home">
          <img className="wordmark-icon" src="/brand/house-mark.png" alt="" />
          <span className="wordmark-type">House<span>Space</span></span>
        </a>
        <nav className="sp-header-nav" aria-label="Service page navigation">
          <a className="sp-nav-link" href="/#services">All services</a>
          <a className="sp-nav-link sp-nav-phone" href={`tel:${phoneLink}`}><Phone size={15} /> {phone}</a>
          <a className="button button-small button-dark" href="/#estimate">Get a free estimate <ArrowUpRight size={16} /></a>
        </nav>
      </header>

      <main id="service-content" tabIndex={-1}>
        <nav className="sp-crumbs section-pad" aria-label="Breadcrumb">
          <ol>
            <li><a href="/">Home</a></li>
            <li><a href="/#services">Services</a></li>
            <li aria-current="page">{service.title}</li>
          </ol>
        </nav>

        <section className="sp-hero section-pad">
          <div className="sp-hero-copy">
            <p className="eyebrow eyebrow-dark"><span /> SERVICE {number} · CENTRAL & SOUTHWEST OHIO</p>
            <h1>{service.title} <em>in Ohio</em></h1>
            <p className="sp-summary">{service.summary}</p>
            <div className="hero-actions">
              <a className="button button-accent" href="/#estimate">Request a free estimate <ArrowUpRight size={17} /></a>
              <a className="text-link" href={`tel:${phoneLink}`}><Phone size={16} /> Call {phone}</a>
            </div>
          </div>
          <figure className="sp-figure">
            <div className="sp-figure-frame">
              <ResponsiveImage
                asset={service.image as ImageAssetName}
                sizes="(max-width: 780px) calc(100vw - 48px), 46vw"
                alt={service.alt}
                fetchPriority="high"
                decoding="async"
              />
              <span className="sp-inspo-tag">INSPIRATION</span>
            </div>
            <figcaption>Inspiration photograph. Not a verified portfolio of HouseSpace projects.</figcaption>
          </figure>
        </section>

        <section className="sp-scope section-pad" aria-labelledby="scope-heading">
          <div className="sp-scope-inner">
            <div className="sp-scope-head">
              <p className="eyebrow"><span /> HOW WE TALK IT THROUGH</p>
              <h2 id="scope-heading">Planning your <em>{service.title.toLowerCase()}</em> project</h2>
            </div>
            <ol className="sp-steps">
              {service.scope.map((item, i) => (
                <li key={item}><span className="sp-step-num">0{i + 1}</span><p>{item}</p></li>
              ))}
            </ol>
            <div className="sp-planning">
              <h3>Before you reach out</h3>
              <p>{service.planning}</p>
            </div>
          </div>
        </section>

        <section className="sp-faq section-pad" aria-labelledby="faq-heading">
          <div className="sp-faq-inner">
            <div className="sp-faq-head">
              <p className="eyebrow eyebrow-dark"><span /> QUESTIONS</p>
              <h2 id="faq-heading">Common <em>questions</em></h2>
              <p>Straight answers about {service.title.toLowerCase()} and working with HouseSpace.</p>
            </div>
            <div className="sp-faq-list">
              {questions.map((q) => (
                <details key={q.question}>
                  <summary><span>{q.question}</span><span className="sp-faq-icon" aria-hidden="true" /></summary>
                  <p>{q.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="sp-business section-pad" aria-label="About HouseSpace">
          <div className="sp-business-inner">
            <div><ShieldCheck size={22} aria-hidden="true" /><p><strong>House Space Design LLC</strong>Licensed Home Improvement General Contractor. Fully insured.</p></div>
            <div><MapPin size={22} aria-hidden="true" /><p><strong>Central & Southwest Ohio</strong>Based in Columbus, serving Columbus, Dayton, Cincinnati and nearby communities.</p></div>
            <div><Clock3 size={22} aria-hidden="true" /><p><strong>Business hours</strong>Monday–Friday: 9:00 AM–5:00 PM. Saturday & Sunday: Closed.</p></div>
          </div>
        </section>

        <section className="sp-cta section-pad" aria-labelledby="cta-heading">
          <div className="sp-cta-inner">
            <div>
              <p className="eyebrow"><span /> YOUR NEXT STEP</p>
              <h2 id="cta-heading">Talk with us about <em>{service.title.toLowerCase()}.</em></h2>
            </div>
            <div className="sp-cta-actions">
              <a className="button button-accent" href="/#estimate">Request a free estimate <ArrowUpRight size={17} /></a>
              <a className="sp-cta-link" href={`tel:${phoneLink}`}><Phone size={17} /> {phone}</a>
              <a className="sp-cta-link" href={`mailto:${email}`}><Mail size={17} /> {email}</a>
            </div>
          </div>
        </section>

        <nav className="sp-others section-pad" aria-labelledby="others-heading">
          <div className="sp-others-inner">
            <h2 id="others-heading">All HouseSpace services</h2>
            <ul>
              {servicePages.map((page, i) => {
                const current = page.slug === service.slug;
                return (
                  <li key={page.slug}>
                    <a href={`/services/${page.slug}/`} aria-current={current ? "page" : undefined} className={current ? "is-current" : undefined}>
                      <span className="sp-other-num">0{i + 1}</span>
                      <span className="sp-other-title">{page.title}</span>
                      <ArrowRight size={17} aria-hidden="true" />
                    </a>
                  </li>
                );
              })}
            </ul>
            <a className="text-link" href="/#services"><ArrowLeft size={16} /> Back to services overview</a>
          </div>
        </nav>
      </main>

      <footer className="site-footer">
        <div className="footer-top">
          <div className="footer-brand">
            <a className="wordmark footer-wordmark" href="/">
              <img className="wordmark-icon" src="/brand/house-mark.png" alt="" />
              <span className="wordmark-type">House<span>Space</span></span>
            </a>
            <p>Thoughtful construction & remodeling<br />for homes and businesses in Central and Southwest Ohio.</p>
            <p>House Space Design LLC · Columbus, Ohio<br />Fully insured.</p>
          </div>
          <div className="footer-links">
            <span className="footer-label">Find us</span>
            <a href="https://maps.google.com/?q=1306+West+Broad+St+Columbus+OH+43222" target="_blank" rel="noreferrer">1306 West Broad St<br />Columbus, OH 43222 <ArrowUpRight size={14} /></a>
          </div>
          <div className="footer-links">
            <span className="footer-label">Talk with our team</span>
            <a href={`tel:${phoneLink}`}>{phone} <ArrowUpRight size={14} /></a>
            <a href={`mailto:${email}`}>{email} <ArrowUpRight size={14} /></a>
          </div>
          <div className="footer-links footer-nav">
            <span className="footer-label">Explore</span>
            <a href="/#services">What we do</a>
            <a href="/#estimate">Free estimate</a>
          </div>
        </div>
        <div className="footer-bottom">
          <span>© HouseSpace. All rights reserved.</span>
          <a href="/privacy-policy/">Privacy policy</a>
          <a href="#service-content">Back to top</a>
        </div>
      </footer>
    </>
  );
}
